import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/pages/auth/api";
import type { UserActivityEvent } from "@/pages/profile/api";
import { getProfileActivity } from "@/pages/profile/api";
import { useI18n, type Language } from "@/shared/i18n/LanguageContext";

type ProfileTimelineProps = {
  username: string;
  embedded?: boolean;
};

type TimelineGroup = {
  dayKey: string;
  label: string;
  reports: TimelineReport[];
};

type TimelineMetric = {
  label: string;
  value: number | string;
  tone?: "good" | "bad" | "neutral";
};

type TimelineReport = {
  id: string;
  kind: "livestream" | "game" | "moves" | "profile" | "other";
  mark: string;
  label: string;
  metrics: TimelineMetric[];
};

function formatDayLabel(dayKey: string, language: Language) {
  const formatter = new Intl.DateTimeFormat(language === "zh" ? "zh-CN" : "en-US", {
    month: language === "zh" ? "long" : "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  });
  const label = formatter.format(new Date(`${dayKey}T12:00:00Z`));
  return language === "zh" ? label : label.toUpperCase();
}

function groupEvents(
  events: UserActivityEvent[],
  language: Language,
  labels: ReturnType<typeof useI18n>["copy"]["profile"]["activity"],
): TimelineGroup[] {
  const groups = new Map<string, UserActivityEvent[]>();
  for (const event of events) {
    groups.set(event.day_key_et, [...(groups.get(event.day_key_et) ?? []), event]);
  }

  return Array.from(groups.entries()).map(([dayKey, group]) => ({
    dayKey,
    label: formatDayLabel(dayKey, language),
    reports: dailyReports(dayKey, group, labels),
  }));
}

function dailyReports(
  dayKey: string,
  events: UserActivityEvent[],
  labels: ReturnType<typeof useI18n>["copy"]["profile"]["activity"],
): TimelineReport[] {
  const count = (eventType: string) => events.filter((event) => event.event_type === eventType).length;
  const livestreamRight = count("livestream_guess_correct");
  const livestreamWrong = count("livestream_guess_wrong");
  const livestreamTotal = livestreamRight + livestreamWrong;
  const gamesCreated = count("play_game_created");
  const movesSubmitted = count("play_move_submitted");
  const gamesFinished = count("play_game_finished");
  const profileUpdates = count("profile_updated");
  const reports: TimelineReport[] = [];

  if (livestreamTotal > 0) {
    reports.push({
      id: `${dayKey}:livestream`,
      kind: "livestream",
      mark: "LS",
      label:
        labels.did === "Did"
          ? `${labels.did} ${livestreamTotal} ${plural(labels.livestreamGuess, livestreamTotal, labels.livestreamGuesses)}`
          : `${labels.did} ${livestreamTotal} ${labels.livestreamGuesses}`,
      metrics: [
        { value: livestreamRight, label: labels.right, tone: "good" },
        { value: livestreamWrong, label: labels.wrong, tone: "bad" },
      ],
    });
  }

  if (gamesCreated > 0) {
    reports.push({
      id: `${dayKey}:games-created`,
      kind: "game",
      mark: "VS",
      label:
        labels.started === "Started"
          ? `${labels.started} ${gamesCreated} ${plural(labels.modelGame, gamesCreated, labels.modelGames)}`
          : `${labels.started} ${gamesCreated} ${plural(labels.modelGame, gamesCreated, labels.modelGames)}`,
      metrics: [],
    });
  }

  if (movesSubmitted > 0) {
    reports.push({
      id: `${dayKey}:moves`,
      kind: "moves",
      mark: "MV",
      label:
        labels.provided === "Provided"
          ? `${labels.provided} ${movesSubmitted} ${plural(labels.modelMove, movesSubmitted, labels.modelMoves)}`
          : `${labels.provided} ${movesSubmitted} ${plural(labels.modelMove, movesSubmitted, labels.modelMoves)}`,
      metrics: [],
    });
  }

  if (gamesFinished > 0) {
    reports.push({
      id: `${dayKey}:games-finished`,
      kind: "game",
      mark: "END",
      label:
        labels.finished === "Finished"
          ? `${labels.finished} ${gamesFinished} ${plural(labels.modelGame, gamesFinished, labels.modelGames)}`
          : `${labels.finished} ${gamesFinished} ${plural(labels.modelGame, gamesFinished, labels.modelGames)}`,
      metrics: [],
    });
  }

  if (profileUpdates > 0) {
    reports.push({
      id: `${dayKey}:profile`,
      kind: "profile",
      mark: "ID",
      label:
        profileUpdates === 1
          ? labels.updatedProfile
          : labels.updatedProfileTimes.replace("{count}", String(profileUpdates)),
      metrics: [],
    });
  }

  if (reports.length > 0) {
    return reports;
  }

  return events.map((event) => ({
    id: event.event_id,
    kind: "other",
    mark: "EV",
    label: event.summary,
    metrics: [],
  }));
}

function plural(word: string, count: number, pluralWord?: string) {
  if (count === 1) {
    return word;
  }
  if (pluralWord) {
    return pluralWord;
  }
  if (word.endsWith("s")) {
    return `${word}es`;
  }
  return `${word}s`;
}

export function ProfileTimeline({ username, embedded = false }: ProfileTimelineProps) {
  const { copy, language } = useI18n();
  const labels = copy.profile.activity;
  const [events, setEvents] = useState<UserActivityEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "loading-more" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError(null);
    setEvents([]);
    setNextCursor(null);

    const load = async () => {
      try {
        const page = await getProfileActivity(username);
        if (cancelled) {
          return;
        }
        setEvents(page.items);
        setNextCursor(page.next_cursor ?? null);
        setStatus("ready");
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        if (loadError instanceof ApiError && loadError.status === 404) {
          setEvents([]);
          setNextCursor(null);
          setStatus("ready");
          return;
        }
        setError(loadError instanceof Error ? loadError.message : labels.failed);
        setStatus("error");
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [labels.failed, username]);

  const groups = useMemo(() => groupEvents(events, language, labels), [events, labels, language]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || status === "loading" || status === "loading-more") {
      return;
    }
    setStatus("loading-more");
    setError(null);
    try {
      const page = await getProfileActivity(username, nextCursor);
      setEvents((current) => [...current, ...page.items]);
      setNextCursor(page.next_cursor ?? null);
      setStatus("ready");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : labels.failed);
      setStatus("error");
    }
  }, [labels.failed, nextCursor, status, username]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !nextCursor || status === "loading-more" || status === "error") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadMore();
        }
      },
      { rootMargin: "320px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, nextCursor, status]);

  const content = (
    <>
      {status === "loading" ? (
        <div className="profile-timeline profile-timeline--loading" aria-busy="true" aria-label={labels.loading}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="profile-timeline__skeleton" />
          ))}
        </div>
      ) : null}

      {status !== "loading" && status !== "error" && groups.length === 0 ? (
        <p className="profile-status">{labels.empty}</p>
      ) : null}

      {groups.length > 0 ? (
        <div className="profile-timeline">
          {groups.map((group) => (
            <section key={group.dayKey} className="profile-timeline__day" aria-label={group.dayKey}>
              <div className="profile-timeline__date">{group.label}</div>
              <div className="profile-timeline__events">
                {group.reports.map((report) => (
                  <div key={report.id} className="profile-timeline__event" data-report-kind={report.kind}>
                    <span className="profile-timeline__sigil" aria-hidden="true">
                      {report.mark}
                    </span>
                    <strong>{report.label}</strong>
                    {report.metrics.length > 0 ? (
                      <div className="profile-timeline__metrics">
                        {report.metrics.map((metric) => (
                          <span key={metric.label} data-tone={metric.tone ?? "neutral"}>
                            <strong>{metric.value}</strong> {metric.label}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : null}

      {error ? <p className="profile-status">{error}</p> : null}
      {status === "loading-more" ? <p className="profile-status">{labels.loadingMore}</p> : null}
      <div ref={sentinelRef} className="profile-scroll-sentinel" aria-hidden="true" />
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <article className="profile-section profile-timeline-section">
      <div className="profile-section__head">
        <h2>{labels.activity}</h2>
        <span>{labels.et}</span>
      </div>

      {content}
    </article>
  );
}
